import { useQuery } from '@tanstack/react-query';
import { useTemplateSecrets } from '@backstage/plugin-scaffolder-react';
import { QUERY_KEYS } from '../../queryKeys';

export type AzureResourceGroup = {
  id: string;
  name: string;
  location: string;
};

const AZ_USER_TOKEN_SECRET = 'AZURE_AZ_USER_TOKEN';
const ARM_API_VERSION = '2021-04-01';

type ArmResourceGroup = {
  id: string;
  name: string;
  location: string;
};

export const useAzureResourceGroups = (subscriptionId: string | undefined) => {
  // Relies on AzUserTokenExtension having populated `AZURE_AZ_USER_TOKEN` in
  // template secrets earlier in the same template run. The query stays
  // disabled until the secret is present and a subscription has been selected.
  const { secrets } = useTemplateSecrets();
  const token = secrets[AZ_USER_TOKEN_SECRET];
  const hasToken = Boolean(token);

  const getResourceGroups = async (): Promise<AzureResourceGroup[]> => {
    const response = await fetch(
      `https://management.azure.com/subscriptions/${subscriptionId}/resourceGroups?api-version=${ARM_API_VERSION}`,
      { headers: { Authorization: `Bearer ${token}` } },
    );
    if (!response.ok) {
      throw new Error(`Failed to fetch Azure resource groups: ${response.statusText}`);
    }
    const data: { value: ArmResourceGroup[] } = await response.json();
    return data.value
      .map(({ id, name, location }) => ({ id, name, location }))
      .sort((a, b) => a.name.localeCompare(b.name));
  };

  const {
    data: resourceGroups,
    isLoading,
    error,
  } = useQuery({
    queryKey: QUERY_KEYS.scaffolder.azureResourceGroups(subscriptionId ?? ''),
    queryFn: getResourceGroups,
    enabled: hasToken && Boolean(subscriptionId),
    // Disable caching for this query
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: 'always',
  });

  return { resourceGroups, isLoading, error, hasToken };
};
